import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ReactHighcharts from 'react-highcharts';
import numeral from 'numeral';
import { Dropdown, MenuItem } from 'react-bootstrap';
import {
  orderBy, first, reduce, cloneDeep,
  each, capitalize, find, filter, uniq, keyBy,
} from 'lodash';
import { getIsFontSizeBig } from '../../../App/AppReducer';
import ProgressBar from '../../../../components/progress-bar';

import { getColorFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup, getAccountIndustryFilter } from '../../../../components/search-form/search-form-reducer';
import { format as formatMoney } from '../../../../components/money-display';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { loadAccountIndustries } from '../../AnalysisActions';

// Import Selectors
import { getOpportunities } from '../../AnalysisReducer';

const USER_PREFERENCE_RATE_TYPE = 'outcomes-industry-graph-rate-type';
const USER_PREFERENCE_REPORT_TYPE = 'outcomes-industry-graph-report-type';
const USER_PREFERENCE_FIELD_MAPPING_TYPE = 'outcomes-industry-graph-mapping-type';

class IndustryGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rateType: 'Won', // 'Won' 'Lost', 'No Decision',
      reportType: 'amount', // 'amount', 'count'
      mappingType: null, // check this depend on props
      categories: [],
      loaded: false,
      loading: false,
      industries: [],
    };
    this.setRateType = this.setRateType.bind(this);
    this.setReportType = this.setReportType.bind(this);
    this.setMappingType = this.setMappingType.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const { hasIndustryMappings } = this.props;
    const rateType = userPreferences[USER_PREFERENCE_RATE_TYPE] || 'Won';
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    let mappingType = userPreferences[USER_PREFERENCE_FIELD_MAPPING_TYPE] || 'mapping';
    if (!hasIndustryMappings) {
      mappingType = 'field';
    }
    this.mounted = true;
    this.setState({
      rateType, reportType, mappingType,
    }, () => {
      this.loadData()
        .then(
          () => {
            this.calculate();
          },
        );
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  loadData() {
    if (!this.industriesPromise) {
      this.setState({ loading: true, loaded: false, industries: [] });
      this.industriesPromise = this.props.dispatch(loadAccountIndustries());      
    }
    this.industriesPromise.then(
      (industries) => {
        if (this.mounted) {
          this.setState({ loading: false, loaded: true, industries });
        }
      },
      () => {

      },
    );
    return this.industriesPromise;
  }

  getRateTypeLable(rateType) {
    if (rateType === 'Won') {
      return 'Win Rate';
    } else if (rateType === 'Lost') {
      return 'Loss Rate';
    }
    return `${rateType} Rate`;
  }

  setRateType(rateType) {
    this.setState({ rateType });
    this.context.updateUserPreference(USER_PREFERENCE_RATE_TYPE, rateType);
  }

  setReportType(reportType) {
    this.setState({ reportType });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
    const categories = orderBy(this.state.categories, reportType, ['desc']);
    this.setState({ categories });
  }

  setMappingType(mappingType) {
    this.setState({ mappingType }, () => {
      this.calculate();
    });
    this.context.updateUserPreference(USER_PREFERENCE_FIELD_MAPPING_TYPE, mappingType);
  }

  calculate() {
    // switch the categories
    const { accountIndustryMappings, opportunities } = this.props;
    const { mappingType, reportType, industries } = this.state;

    let categories = [];
    const oneStat = {
      count: 0,
      amount: 0,
      average: 0,
      counts: { Won: 0, Lost: 0, 'No Decision': 0 },
      amounts: { Won: 0, Lost: 0, 'No Decision': 0 },
      countRates: { Won: 0, Lost: 0, 'No Decision': 0 },
      amountRates: { Won: 0, Lost: 0, 'No Decision': 0 },
      opportunityIds: { Won: [], Lost: [], 'No Decision': [] },
    };

    if (mappingType === 'mapping') {
      categories = reduce(accountIndustryMappings, (items, mapping) => items.concat({
        id: mapping._id,
        label: mapping.label,
        values: mapping.values,
        ...(cloneDeep(oneStat)),
      }), []);
      
    } else {
      categories = reduce(industries, (items, val) => items.concat({
        id: val._id,
        label: val.label,
        values: [val.industry_id],
        ...(cloneDeep(oneStat)),
      }), []);
    }


    const indexedCategories = keyBy(categories, 'id');

    const reverseMappings = reduce(categories, (items, category) => {
      each(category.values, (value) => {
        if (!items[value]) {
          items[value] = [];
        }
        items[value] = uniq(items[value].concat(category.id));
      });
      return items;
    }, {});

    const indexByLabel = keyBy(industries, 'label');
    const indexByIds = keyBy(industries, 'industry_id');

    each(opportunities, (opportunity) => {
      const accountIndustryLabels = opportunity.account ? opportunity.account.industries : [];
      const { stage } = opportunity;
      const amount = opportunity.amount || 0;
      const oCategories = reduce(accountIndustryLabels, (items, accountIndustryLabel) => {
        const industry = indexByLabel[accountIndustryLabel] || indexByIds[accountIndustryLabel];
        if (industry) {
          if (reverseMappings[industry.industry_id]) {
            return uniq(items.concat(reverseMappings[industry.industry_id]));
          }
        }
        return items;
      }, []);

      each(oCategories, (category) => {
        if (indexedCategories[category]) {
          indexedCategories[category].count += 1;
          indexedCategories[category].amount += amount;

          if (indexedCategories[category].counts[stage] !== undefined) {
            indexedCategories[category].counts[stage] += 1;
          }
          if (indexedCategories[category].amounts[stage] !== undefined) {
            indexedCategories[category].amounts[stage] += amount;
          }
          if (indexedCategories[category].opportunityIds[stage] !== undefined) {
            indexedCategories[category].opportunityIds[stage].push(opportunity.opportunity_id);
          }
        }
      });
    });

    const nonEmptyCategories = filter(categories, category => category.count > 0);
    const sortedCategories = orderBy(nonEmptyCategories, reportType, ['desc']);


    each(sortedCategories, (sortedCategory) => {
      each(sortedCategory.countRates, (v, k) => {
        sortedCategory.countRates[k] = sortedCategory.count !== 0 ? (sortedCategory.counts[k] / sortedCategory.count) * 100 : 0;
      });
      each(sortedCategory.amountRates, (v, k) => {
        sortedCategory.amountRates[k] = sortedCategory.amount !== 0 ? (sortedCategory.amounts[k] / sortedCategory.amount) * 100 : 0;
      });
      sortedCategory.average = sortedCategory.count !== 0 ? (sortedCategory.amount / sortedCategory.count) : 0;
    });
    this.setState({ categories: sortedCategories });
  }


  afterRender(chart) {
    const ext = chart.yAxis[1].getExtremes();
    const dMax = Math.abs(ext.dataMax);
    const dMin = Math.abs(ext.dataMin);
    const dExt = dMax >= dMin ? dMax : dMin;
    const min = 0 - dExt;
    chart.yAxis[1].setExtremes(min, dExt);
    chart.yAxis[0].setExtremes(0, 100);
  }

  renderGraph() {
    const {
      opportunities, hasNoDecisionSetup, organization,
      isFontSizeBig,
    } = this.props;
    if (opportunities.length === 0) {
      return null;
    }
    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const {
      categories, reportType, rateType,
    } = this.state;

    const rateLabel = this.getRateTypeLable(rateType);
    let yAxisTitle = capitalize(reportType);
    if (reportType === 'amount' && suffix && suffix.length) {
      yAxisTitle += ` (${suffix})`;
    }

    const series = [];
    if (hasNoDecisionSetup) {
      series.push({
        name: 'No Decision', color: getColorFromKey('noDecision'), data: [], yAxis: 1,
      });
    }
    series.push({
      name: 'Lost', color: getColorFromKey('lost'), data: [], yAxis: 1,
    });
    series.push({
      name: 'Won', color: getColorFromKey('won'), data: [], yAxis: 1,
    });

    const rateSeries = {
      name: rateLabel,
      color: '#0c7cd5',
      data: [],
      yAxis: 0,
      type: 'line',
      marker: {
        symbol: 'circle',
      },
    };
    const avgSeries = {
      name: 'Avg deal size',
      color: '#ffffff',
      lineColor: 'transparent',
      yAxis: 1,
      marker: {
        fillColor: 'transparent', // make the line markers invisible
        lineWidth: 0,
        radius: 0,
        lineColor: 'transparent',
        states: {
          hover: {
            enabled: false, // prevent the highlight circle from showing when hovered
          },
        },
      },
      data: [],
      type: 'line',
      showInLegend: false,
    };
    each(categories, (category) => {
      each(series, (s) => {
        const stage = s.name;
        const modifier = stage === 'Won' ? 1 : -1;
        let y = 0;
        if (category[reportType === 'amount' ? 'amounts' : 'counts'][stage]) {
          y = modifier * (category[reportType === 'amount' ? 'amounts' : 'counts'][stage] || 0);
        }
        const absY = Math.abs(y);
        let formatedY = 0;
        if (absY > 0) {
          if (reportType === 'amount') {
            formatedY = formatMoney(absY, true, 1, symbol, suffix);
          } else {
            formatedY = numeral(absY).format('0');
          }
        }
        s.data.push({
          y,
          formatedY,
          opportunityIds: category.opportunityIds[stage],
          events: {
            click: (event) => {
              this.props.onBarSelect(event.point.options.opportunityIds);
            },
          },
        });
      });
      const formatedAverage = formatMoney(category.average, true, 1, symbol, suffix);
      const rateY = category[reportType === 'amount' ? 'amountRates' : 'countRates'][rateType] || 0; // cheat
      rateSeries.data.push({ y: rateY, formatedY: `${numeral(rateY).format('0.0')}%` });
      avgSeries.data.push({ y: 0, formatedY: formatedAverage });
    });
    series.push(rateSeries);
    series.unshift(avgSeries);

    const rateColor = '#0c7cd5';

    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        type: 'bar',
        height: Math.max(isFontSizeBig ? 600 : 400, 100 + (categories.length * (isFontSizeBig ? 40 : 30))),
        animation: {
          duration: 1000,
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      xAxis: {
        categories,
        crosshair: true,
        alternateGridColor: 'rgba(66,66,66,0.05)',
        labels: {
          formatter: function f() {
            return `${this.value.label}`;
          },
          style: {
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
      },
      tooltip: {
        headerFormat: `<div><span style="font-size:16px;font-weight: bold;">{point.key.label}</span></div><table style="${isFontSizeBig ? 'font-size: 16px;' : ''};margin-top: 1em; margin-bottom: 0;" class="table table-condensed">`,
        pointFormat: `<tr>
          <td style="color:{series.color};">{series.name}:&nbsp;</td>
          <td class="text-right"><b>{point.formatedY}</b></td>
        </tr>`,
        footerFormat: '</table>',
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
        positioner: function p(labelWidth, labelHeight, point) {
          let tooltipX = 0;
          let tooltipY = 0;
          const { chart } = this;
          const marginX = 20;
          const marginY = 20;
          if (point.plotX + labelWidth > chart.plotWidth) {
            tooltipX = (point.plotX + chart.plotLeft) - labelWidth - marginX;
          } else {
            tooltipX = point.plotX + chart.plotLeft + marginX;
          }

          if ((point.plotY + chart.plotTop) - labelHeight - marginY > 0) {
            tooltipY = (point.plotY + chart.plotTop) - labelHeight - marginY;
          } else {
            tooltipY = point.plotY + chart.plotTop + marginY;
          }

          return {
            x: tooltipX,
            y: tooltipY,
          };
        },
      },
      yAxis: [
        {
          gridLineWidth: 0,
          max: 100,
          min: 0,
          allowDecimals: false,
          title: {
            text: rateLabel,
            style: {
              color: rateColor,
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          labels: {
            formatter: function f() {
              return `${Math.min(100, this.value)}%`;
            },
            style: {
              color: rateColor,
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
        },
        {
          title: {
            text: yAxisTitle,
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
              fontWeight: 'bold',
            },
          },
          opposite: true,
          tickAmount: 9,
          labels: {
            formatter: function f() {
              let v = 0;
              if (this.value !== 0) {
                if (reportType === 'amount') {
                  v = formatMoney(this.value, true, 1, symbol);
                } else {
                  v = numeral(this.value).format('0');
                }
              }
              return v;
            },
            style: {
              fontSize: isFontSizeBig ? '16px' : null,
            },
          },
          lineWidth: 0,
          plotLines: [{
            value: 0,
            dashStyle: 'dashed',
            color: 'rgba(0, 0, 0, 0.5)',
            width: 2,
            label: {
              text: null,
            },
          }],
        },
      ],
      plotOptions: {
        series: {
          stacking: 'normal',
          marker: {
            fillColor: '#ffffff',
            lineWidth: 2,
            radius: 5,
            lineColor: null, // inherit from series
          },
        },
      },
      legend: {
        reversed: true,
        itemStyle: {
          fontSize: isFontSizeBig ? '16px' : null,
        },
      },
      series,
    };

    return (
      <div>
        <ReactHighcharts config={config} isPureConfig callback={this.afterRender} />
      </div>
    );
  }

  render() {
    const {
      opportunities, hasNoDecisionSetup, hasIndustryMappings,
    } = this.props;
    if (opportunities.length === 0) {
      return null;
    }
    const {
      rateType, reportType, mappingType, loading, loaded,
    } = this.state;

    if (loading || !loaded) {
      return (
        <ProgressBar />
      );
    }
    const stages = [
      { value: 'Won', label: 'Win Rate' },
      { value: 'Lost', label: 'Loss Rate' },
    ];
    if (hasNoDecisionSetup) {
      stages.push({ value: 'No Decision', label: 'No Decision Rate' });
    }
    const activeStage = find(stages, { value: rateType }) || { label: 'Rate' };

    const reportTypes = [
      { value: 'amount', label: 'Amount' },
      { value: 'count', label: 'Count' },
    ];
    const activeReportType = find(reportTypes, { value: reportType }) || first(reportTypes);

    const mappingTypes = [
      { value: 'field', label: 'Fields', disabled: false },
      { value: 'mapping', label: 'Mappings', disabled: !hasIndustryMappings },
    ];
    let activeMappingType = find(mappingTypes, { value: mappingType }) || first(mappingTypes);
    if (!hasIndustryMappings) {
      activeMappingType = { value: 'field', label: 'Fields' };
    }
    return (
      <div>
        <div className="animated fadeIn">
          <div>
            <h5 className="sub-header-with-controls">
              View&nbsp;
              <Dropdown
                id="stage-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeStage.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {stages.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === rateType}
                      onSelect={() => { this.setRateType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>by</span>
              &nbsp;
              <Dropdown
                id="report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeReportType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {reportTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === activeReportType.value}
                      onSelect={() => { this.setReportType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>and use industry</span>
              &nbsp;
              <Dropdown
                id="report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeMappingType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {mappingTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === activeMappingType.value}
                      onSelect={() => { this.setMappingType(s.value); }}
                      disabled={s.disabled}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
            </h5>
          </div>
          {this.renderGraph()}
        </div>
      </div>
    );
  }
}

IndustryGraph.propTypes = {
  dispatch: PropTypes.func.isRequired,
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  organization: PropTypes.shape({
    organization_id: PropTypes.string.isRequired,
  }),
  accountIndustryMappings: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.string).isRequired,
  })).isRequired,
  hasIndustryMappings: PropTypes.bool.isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  onBarSelect: PropTypes.func,
  isFontSizeBig: PropTypes.bool.isRequired,
};

IndustryGraph.defaultProps = {
  organization: null,
  onBarSelect: () => {},
};

IndustryGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  const accountIndustryFilter = getAccountIndustryFilter(state);
  const accountIndustryMappings = accountIndustryFilter ? accountIndustryFilter.mappings : [];
  const hasIndustryMappings = (filter(accountIndustryMappings, m => m.values.length > 0)).length > 0;
  return {
    opportunities: getOpportunities(state),
    organization: getUserCurrentOrganization(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    accountIndustryMappings,
    hasIndustryMappings,
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

export default connect(mapStateToProps)(IndustryGraph);
